/**
 * `AgentRegistryView` — ADR-0229 ops2026 "Agent registry" board.
 *
 * The weekly discovery job proposes unregistered bot names (with
 * evidence), flags rotation spoof clusters and dormant registered agents.
 * Admins approve (→ edge AGENT_REGISTRY KV), reject or snooze. The
 * Manual / Automatic toggle (`agent_registry_auto`, default Manual) lives
 * here, like the purge mode lives on the Purge board.
 *
 * ADR-0230: AI classification on every candidate (pre-filled approve
 * form), plain-words kind explanations, AI-assisted Automatic (daily,
 * ≤ 50 a day, Auto badge + Revoke in History), and operator IP list
 * freshness.
 *
 * Degrades gracefully when the API is older than the SPA (the admin SPA
 * auto-deploys on merge; the API deploys separately): a 404 renders a
 * clear "not deployed yet" card instead of a broken page.
 */

import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { AdminAgentRegistryResponse } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { Pill } from '../../components/atoms/Pill.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { JobLastRunNote } from '../jobs/JobLastRunNote.js';
import { AI_KIND_EXPLANATION, AI_KIND_LABEL, AI_REGISTRY_KINDS } from '@sill/types';

import { HistoryRow } from './HistoryRow.js';
import { ProposalCard } from './ProposalCard.js';
import { AUTO_MODE_RULES, COMPANY_HELP, IMPERSONATION_LABEL, KIND_GROUPS_HELP, SILL_INTERNAL_NOTE, isApiMissing, shortKey } from './registry-helpers.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export function AgentRegistryView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [data, setData] = useState<AdminAgentRegistryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [togglePending, setTogglePending] = useState(false);
  const [autoConfirmOpen, setAutoConfirmOpen] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getAgentRegistry());
      setMissing(false);
    } catch (err) {
      if (isApiMissing(err)) setMissing(true);
      else setError(err instanceof ApiError ? err.message : 'Failed to load the agent registry.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyToggle = async (next: boolean): Promise<void> => {
    setTogglePending(true);
    try {
      await api.setSetting('agent_registry_auto', next);
      setAutoConfirmOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError && err.kind === 'forbidden' ? 'Admin role required to change the mode.' : 'Failed to change mode — retry.');
    } finally {
      setTogglePending(false);
    }
  };

  const pendingBots = (data?.pending ?? []).filter((p) => p.ai_classification?.kind !== 'human_browser');
  const aiSaysBrowser = (data?.pending ?? []).filter((p) => p.ai_classification?.kind === 'human_browser');

  const kicker = { fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' as const, marginBottom: 8 };
  const section = (title: string, count: number): JSX.Element => (
    <div style={{ ...kicker, marginTop: 24 }}>
      {title} · {count}
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1040 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={kicker}>ADR-0229 · Agent discovery</div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Agent registry</h1>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: sansFont, fontSize: 12, padding: '6px 12px', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {missing ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body }}>
            The agent registry API is not deployed yet. This page will work once the API release that adds it is live; nothing is broken in the meantime.
          </div>
        </Card>
      ) : null}

      {error !== null ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div role="alert" style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
        </Card>
      ) : null}

      {loading && data === null ? <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div> : null}

      {data !== null ? (
        <>
          <Card style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontFamily: sansFont, fontSize: 13.5, fontWeight: 600, color: C.text }}>
                  Approval mode: {data.auto_mode ? 'Automatic' : 'Manual (approval required)'}
                </div>
                <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, marginTop: 2, maxWidth: '64ch' }}>
                  {data.auto_mode
                    ? `Automatic is on. Once a day it approves candidates that pass every rule below (at most ${data.auto_daily_cap ?? 10} a day; ${data.auto_today ?? 0} in the last 24 hours). Each gets an Auto badge and a Revoke button in History. Everything else waits here.`
                    : 'Manual: the AI classifies every candidate and pre-fills the form, but nothing reaches the edge registry until you click Approve. Look-alike names show a warning; you can approve anyway.'}{' '}
                  {data.bundled_count} identities are built into the edge.
                </div>
                <ul data-testid="auto-rules" style={{ margin: '8px 0 0', paddingLeft: 18, fontFamily: sansFont, fontSize: 12, color: C.muted, maxWidth: '70ch' }}>
                  {AUTO_MODE_RULES.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => (data.auto_mode ? void applyToggle(false) : setAutoConfirmOpen(true))}
                disabled={togglePending}
                style={{ fontFamily: sansFont, fontSize: 12.5, fontWeight: 600, padding: '8px 14px', whiteSpace: 'nowrap', background: data.auto_mode ? 'transparent' : C.lime, color: data.auto_mode ? C.body : C.ink, border: `1px solid ${data.auto_mode ? C.border : C.lime}`, borderRadius: 3, cursor: 'pointer', opacity: togglePending ? 0.6 : 1 }}
              >
                {data.auto_mode ? 'Switch to manual' : 'Switch to automatic'}
              </button>
            </div>
          </Card>

          <Card style={{ padding: 14, marginBottom: 8 }}>
            <div data-testid="kind-help" style={{ fontFamily: sansFont, fontSize: 12.5, color: C.body }}>
              <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>Agents, bots and people</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {KIND_GROUPS_HELP.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: 'pointer', color: C.muted }}>What each AI kind means</summary>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: C.muted }}>
                  {AI_REGISTRY_KINDS.map((k) => (
                    <li key={k}>
                      <strong style={{ color: C.body }}>{AI_KIND_LABEL[k]}</strong> — {AI_KIND_EXPLANATION[k]}
                    </li>
                  ))}
                </ul>
              </details>
              <div style={{ marginTop: 6, color: C.muted }}>{COMPANY_HELP}</div>
              <div style={{ marginTop: 4, color: C.muted }}>{SILL_INTERNAL_NOTE}</div>
            </div>
          </Card>

          {section('Pending candidates', pendingBots.length)}
          {pendingBots.length === 0 ? (
            <Card style={{ padding: 14 }}>
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>
                Nothing pending. Candidates are refreshed daily (full review weekly): unregistered bot names with at least 20 hits or seen on 2 or more sites.
              </div>
              <div style={{ marginTop: 8 }}>
                <JobLastRunNote job_name="job:agent_registry_discovery" refreshKey={data} />
              </div>
            </Card>
          ) : (
            pendingBots.map((p) => <ProposalCard key={p.proposal_id} proposal={p} onDecided={() => void load()} />)
          )}

          {aiSaysBrowser.length > 0 ? section('AI says: a person\'s browser', aiSaysBrowser.length) : null}
          {aiSaysBrowser.length > 0 ? (
            <div data-testid="ai-browser-bucket" style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginBottom: 6, maxWidth: '75ch' }}>
              The AI thinks these are people, but Sill&apos;s browser check does not agree, so they are not hidden. Reject them if the AI is right.
            </div>
          ) : null}
          {aiSaysBrowser.map((p) => <ProposalCard key={p.proposal_id} proposal={p} onDecided={() => void load()} />)}

          {section('Spoof clusters', data.spoof_clusters.length)}
          {data.spoof_clusters.map((p) => <ProposalCard key={p.proposal_id} proposal={p} onDecided={() => void load()} />)}

          {section('Impersonation evidence', data.impersonated.length)}
          {data.impersonated.length > 0 ? (
            <TableScroll minWidth={720}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
                <thead>
                  <tr>
                    {['Observed', 'User agent', 'Claims', 'Reason', 'Hits', 'Last'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: C.muted, padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.impersonated.map((r) => (
                    <tr key={r.agent_key}>
                      <td style={{ padding: '6px 8px', fontFamily: monoFont, fontSize: 11, color: C.muted }}>{shortKey(r.agent_key)}</td>
                      <td style={{ padding: '6px 8px', fontFamily: monoFont, fontSize: 11, color: C.body, maxWidth: 360, wordBreak: 'break-all' }}>{r.user_agent}</td>
                      <td style={{ padding: '6px 8px', color: C.body }}>{r.bundled_agent_id ?? r.inferred_name ?? '—'}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <Pill tone="coral">{IMPERSONATION_LABEL[r.impersonation_reason ?? ''] ?? r.impersonation_reason ?? '—'}</Pill>
                      </td>
                      <td style={{ padding: '6px 8px', color: C.text }}>
                        {r.impersonation_hits.toLocaleString()} / {r.hit_count.toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 8px', fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                        {r.impersonation_at !== null ? formatIsoCompact(r.impersonation_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          ) : null}

          {section('Dormant registered agents', data.dormant.length)}
          {data.dormant.map((p) => <ProposalCard key={p.proposal_id} proposal={p} onDecided={() => void load()} />)}

          {data.snoozed.length > 0 ? section('Snoozed', data.snoozed.length) : null}
          {data.snoozed.map((p) => <ProposalCard key={p.proposal_id} proposal={p} onDecided={() => void load()} />)}

          {section('History', data.history.length)}
          {data.history.map((p) => (
            <HistoryRow key={p.proposal_id} proposal={p} onChanged={() => void load()} />
          ))}

          {data.ip_lists !== undefined ? section('Operator IP lists', data.ip_lists.length) : null}
          {data.ip_lists !== undefined ? (
            <>
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginBottom: 6, maxWidth: '75ch' }}>
                Address ranges companies publish for their bots. Sill stores them and refreshes them daily; if a fetch fails, the last good list keeps working. A visit from inside a company&apos;s ranges is recorded as verified by network.
              </div>
              {data.ip_lists.length === 0 ? (
                <Card style={{ padding: 14 }}>
                  <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>No lists stored yet. The daily refresh fills this in.</div>
                  <div style={{ marginTop: 8 }}>
                    <JobLastRunNote job_name="job:operator_ip_ranges_refresh" refreshKey={data} />
                  </div>
                </Card>
              ) : (
                <TableScroll minWidth={720}>
                  <table data-testid="ip-lists" style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
                    <thead>
                      <tr>
                        {['Company', 'List', 'Ranges', 'Last good', 'Last attempt'].map((h) => (
                          <th key={h} style={{ textAlign: 'left', fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: C.muted, padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.ip_lists.map((l) => {
                        const stale = l.last_good_at === null || Date.now() - Date.parse(l.last_good_at) > 3 * 86_400_000;
                        return (
                          <tr key={l.source_url}>
                            <td style={{ padding: '6px 8px', color: C.text }}>{l.company}</td>
                            <td style={{ padding: '6px 8px', fontFamily: monoFont, fontSize: 11, color: C.body, wordBreak: 'break-all', maxWidth: 320 }}>{l.source_url}</td>
                            <td style={{ padding: '6px 8px', color: C.text }}>{l.prefix_count.toLocaleString()}</td>
                            <td style={{ padding: '6px 8px', fontFamily: monoFont, fontSize: 11, color: stale ? C.coralFg : C.muted }}>
                              {l.last_good_at !== null ? formatIsoCompact(l.last_good_at) : 'never'}
                            </td>
                            <td style={{ padding: '6px 8px', fontFamily: monoFont, fontSize: 11, color: l.last_status === 'ok' || l.last_status === 'unchanged' ? C.muted : C.coralFg }}>
                              {l.last_attempt_at !== null ? `${formatIsoCompact(l.last_attempt_at)} · ${l.last_status ?? '—'}` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </TableScroll>
              )}
            </>
          ) : null}
        </>
      ) : null}

      <ConfirmDialog
        open={autoConfirmOpen}
        title="Switch to automatic approvals?"
        tone="coral"
        body={`Once a day, Sill will add names to the edge registry without waiting for you, within these limits: ${AUTO_MODE_RULES.join(' ')} You can revoke any of them, or switch back, at any time.`}
        confirmLabel="Enable automatic"
        pending={togglePending}
        onConfirm={() => void applyToggle(true)}
        onCancel={() => setAutoConfirmOpen(false)}
      />
    </div>
  );
}
